import { Component } from 'react';
import { NavLink } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { inject, observer } from 'mobx-react';
import { Col, Container, Row } from 'react-bootstrap';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

//components
import applicationRouter from '~/hoc/applicationRouter'
import withLocalization from '~/hoc/withLocalization';
import CustomCircularProgressbar from '../../../components/ProgressBar/CircularProgressBar';
import RectangularCardIcon from '../../../components/Card/RectangularCardIcon';
import RectangularCard from '../../../components/Card/RectangularCard';

//widgets
import TimelogWidget from './TimelogWidget';
import DeviationWidget from './DeviationWidget';
import AdminPMScheduleWidget from './AdminPMScheduleWidget';
import MemberScheduleWidget from './MemberScheduleWidget';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';

//configs
import { getUploadedFileURL } from '~/library/config';

//utils
import { totalTimeFormat, get_currency } from '~/utils/dateFormat';
import { getPercentageFromNumberValues, getPercentage } from '~/utils/commonMethods';

//assets
import overtimeHours from '../../../assets/img/new_design/overtimeHours.svg';
import totalCostsSvg from '../../../assets/img/new_design/totalCosts.svg';
import totalHours from '../../../assets/img/new_design/totalHours.svg'

//styles
import './dashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend);

class Dashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            overallWorkhours: {},
            usersCounters: {},
            timesheetCounters: {},
            clients: {},
            users: {},
            grandtotals: {}
        }
    }

    componentDidMount() {
        this.loadData();
    }

    async loadData() {
        const { dashboardStore, commonStore, userStore, t } = this.props;
        const userType = userStore.currentUser.user_type;
        try {
            const data = await dashboardStore.load({
                pmMembers: userType === 'pm' && userStore.currentUser?.Manages?.length ? userStore.currentUser.Manages.join(',') : ''
            });
            const _timesheetCounters = {
                pending_timesheet: 0,
                all_timesheets: 0
            }
            if (data?.timesheetCounters?.length) {
                data.timesheetCounters.forEach(timesheet => {
                    if (timesheet.status === 'active')
                        _timesheetCounters.pending_timesheet += parseInt(timesheet.count);
                    _timesheetCounters.all_timesheets += parseInt(timesheet.count);
                });
            }

            this.setState({
                overallWorkhours: data?.overallWorkhours?.length && data.overallWorkhours[0] || {},
                usersCounters: data?.usersCounters || {},
                timesheetCounters: _timesheetCounters,
                clients: data?.clients?.length && data.clients[0] || {},
                users: data?.users?.length && data.users[0] || {},
                grandtotals: data?.grandtotals || {},
                isLoading: false
            });

            if (commonStore.config.client.data.isFirstTimeLogin === true) {
                this.props.router.navigate('/admin/control_panel')
            }
        }
        catch (error) {
            console.error({ error });
            this.setState({ isLoading: false });
            commonStore.addNotification(t(error.message || "Something went wrong"), null, 'error');
        }
    }

    redirectToAnotherPage(idx, dataCount) {
        if (!dataCount) return;
        switch (idx) {
            case 0:
                this.props.router.navigate('/admin/timelogs', { state: { dashboardRedirected: 'draft' } })
                break;
            case 1:
                this.props.router.navigate('/admin/timelogs', { state: { dashboardRedirected: 'logs_today' } })
                break;
            case 2:
                this.props.router.navigate('/admin/members', { state: { dashboardRedirected: 'users_not_at_work' } })
                break;
            case 3:
                this.props.router.navigate('/admin/timelogs', { state: { dashboardRedirected: 'active' } })
                break;
            default:
                break;
        }
    }

    renderStatusCards(dataObject, isTitleVisible, isAdminBlock = false) {
        const { t } = this.props;
        return (
            <>
                {
                    isTitleVisible && (
                        <Row className='mb-4'>
                            <NavLink to="/admin/clients">
                                <span className='dashboard-title mx-2 p-0'>
                                    {t(dataObject.title)} {t('Status')}
                                </span>
                            </NavLink>

                        </Row>
                    )
                }

                <Row className='mx-4 mx-md-0'>
                    {dataObject.metaData.map((metaDataHeader, index) =>
                        <Col key={uuid()} className={isAdminBlock ? 'd-flex justify-content-center d-md-block col-lg-3 col-sm-6 col-10 offset-sm-0 offset-1' : 'd-flex justify-content-center justify-content-md-start'}>
                            <RectangularCard backgoundClass={`rectangle-card-bg-${dataObject.backgroundColor[index]}`} key={uuid()} className='w-100'>
                                <Row className='align-items-center'>
                                    <Col xs={5} sm={5}>
                                        <span className='d-flex justify-content-center dashboard-progressBar-header-text text-center mt-2'>
                                            {t(metaDataHeader)}
                                        </span>
                                        <span onClick={() => this.redirectToAnotherPage(index, dataObject.data[index])} className={`d-flex justify-content-center cursor-pointer dashboard-progressBar-value dashboard-progressBar-value-color-${dataObject.dataColors[index]}`}>
                                            {dataObject.data[index]}
                                        </span>
                                    </Col>
                                    <Col xs={7} sm={7}>
                                        <span className='d-flex justify-content-center'>
                                            <CustomCircularProgressbar
                                                percentage={dataObject.dataInPercentatge[index]}
                                                pathColor={dataObject.dataColors[index]}
                                                rotationStart={(index + 1) * 2}
                                            />
                                        </span>
                                    </Col>
                                </Row>
                            </RectangularCard>
                        </Col>
                    )
                    }
                </Row>
            </>
        )
    }

    renderSuperAdminDashboard() {
        const { usersCounters, clients, users } = this.state;
        const { inactive: inactiveClients = 0, active: activeClients = 0 } = clients;
        const { inactive: inactiveUsers = 0, active: activeUsers = 0 } = users;
        const { working_now: workingNow = 0, gone_home: goneHome = 0, all_users: allUsers = 0 } = usersCounters;

        const clientStatusData = {
            "title": "Clients",
            "metaData": ["Active", "Inactive"],
            "data": [Number(activeClients), Number(inactiveClients)],
            "dataInPercentatge": getPercentageFromNumberValues([Number(activeClients), Number(inactiveClients)]),
            "dataColors": ["32a852", "E83030"],
            "backgroundColor": ["C3E7C3", "F3DDDD"]
        }
        const userStatusData = {
            "title": "Users",
            "metaData": ["Active", "Inactive"],
            "data": [Number(activeUsers), Number(inactiveUsers)],
            "dataInPercentatge": getPercentageFromNumberValues([Number(activeUsers), Number(inactiveUsers)]),
            "dataColors": ["32a852", "E83030"],
            "backgroundColor": ["C3E7C3", "F3DDDD"]
        }
        const userWorklogStatusData = {
            "title": "Members",
            "metaData": ["Working now", "Gone home", "Not at work"],
            "data": [Number(workingNow), Number(goneHome), Number((allUsers - goneHome) - workingNow)],
            "dataInPercentatge": getPercentageFromNumberValues([Number(workingNow), Number(goneHome), Number((allUsers - goneHome) - workingNow)]),
            "dataColors": ["51459E", "ffa534", "EE2222"],
            "backgroundColor": ["E1E1E1", "ffa534", "F3DDDD"]
        }
        return (
            <Container fluid>
                <Row className='mt-4'>
                    <Col xs={12} sm={12} md={6} lg={4} xl={4} xxl={4}>
                        {this.renderStatusCards(clientStatusData, true)}
                    </Col>
                    <Col xs={12} sm={12} md={6} lg={4} xl={4} xxl={4}>
                        {this.renderStatusCards(userStatusData, true)}
                    </Col>
                    <Col xs={12} sm={12} md={6} lg={4} xl={4} xxl={4}>
                        {this.renderStatusCards(userWorklogStatusData, true)}
                    </Col>
                </Row>
            </Container>
        );
    }

    renderMemberDashboard() {
        const { t, userStore, commonStore } = this.props;
        const { grandtotals } = this.state;
        const { total_normal: totalNormal = 0, total_overtime: totalOvertimes = 0, shiftAllowanceTotal = 0 } = grandtotals;
        const { data: biztypeData } = commonStore.config.biztype;
        const dateTimeRules = commonStore.getDateTimeRules();
        let rate = userStore.currentUser.hourly_rate;
        let earned = 0;

        if (rate && grandtotals
            && (userStore.currentUser.payment_mode !== 'fixed')) {
            earned = parseInt(
                (rate * (totalNormal + totalOvertimes)) / 60 + shiftAllowanceTotal
            );
            earned = `${get_currency(dateTimeRules) === '$' ? `${get_currency(dateTimeRules)} ${earned}` : `${earned} ${get_currency(dateTimeRules)}`}`;
            rate = `${rate} / hrs`;
        }
        else if (userStore.currentUser.payment_mode === 'fixed') {
            earned = `${get_currency(dateTimeRules) === '$' ? `${get_currency(dateTimeRules)} ${rate}` : `${rate} ${get_currency(dateTimeRules)}`}`;
            rate = `${rate} / fixed`;
        }
        else {
            earned = '?';
            rate = t('Rate not set');
        }
        return (
            <>
                <Container className='dashboard-container' fluid style={{ paddingRight: "0px", paddingLeft: "0px" }}>
                    <Row key="main" className="dashboard-stats mx-4 mx-md-0">
                        <TimelogWidget />
                        <Col lg={3} sm={6}>
                            <RectangularCard isFullImage="true" backgoundClass="rectangle-card-bg-white">
                                {
                                    commonStore.config.client.image ? (
                                        <img
                                            src={getUploadedFileURL(commonStore.config.client.image)}
                                            className="img-fluid card-bg-img"
                                            alt={"client"}
                                        />
                                    ) : (
                                        <div className='d-flex align-items-center fw-bold card-bg-img justify-content-center'>{commonStore.config.client.name}</div>
                                    )
                                }
                            </RectangularCard>
                        </Col>

                        <Col lg={3} sm={6}>
                            <RectangularCard backgoundClass="rectangle-card-bg-DFF7E9">
                                <Row>
                                    <Col xs={5} sm={5} md={5}>
                                        <RectangularCardIcon backgoundClass="rectangle-card-icon-bg-C2EDE9">
                                            <img src={overtimeHours} alt="overtimeHours" />
                                        </RectangularCardIcon>
                                    </Col>
                                    <Col xs={7} sm={7} md={7}>
                                        <span className='d-block text-header'>
                                            {t('My total this month')}
                                        </span>
                                        <span className='d-block text-body'>
                                            <small>
                                                {`${totalTimeFormat(totalNormal || 0, dateTimeRules)}`}
                                            </small>hrs
                                        </span>
                                        <span className='d-block text-footer color-0A89FF'>
                                            {`${totalTimeFormat(
                                                totalOvertimes || 0,
                                                dateTimeRules
                                            )} ${t('Overtime')}`}
                                        </span>
                                    </Col>
                                </Row>
                            </RectangularCard>
                        </Col>



                        <Col lg={3} sm={6}>
                            <RectangularCard backgoundClass="rectangle-card-bg-F6DFDF">
                                <Row>
                                    <Col xs={5} sm={5} md={5}>
                                        <RectangularCardIcon backgoundClass="rectangle-card-icon-bg-FEB8B8">
                                            <img src={totalCostsSvg} alt="totalCosts" />
                                        </RectangularCardIcon>
                                    </Col>
                                    <Col xs={7} sm={7} md={7}>
                                        <span className='d-block text-header'>
                                            {t('Earning this month')}
                                        </span>
                                        <span className='d-block text-body'>
                                            {<small>{`${earned}`}</small>}
                                        </span>
                                        <span className='d-block text-footer color-FF8383'>
                                            {`${rate}`}
                                        </span>
                                    </Col>
                                </Row>
                            </RectangularCard>
                        </Col>

                    </Row>

                    {!!biztypeData.enable_deviations && !biztypeData.union_group && (
                        <Row key="deviations" className="deviation">
                            <DeviationWidget />
                        </Row>
                    )}
                    <Row key="schedule">
                        <Col sm={12} lg={12} className="dashboard-schedule">
                            <div className="card-block">
                                <div className="content">
                                    <h4 className='dashboard-title mx-2'>{t('Schedule for next 2 weeks')}</h4>
                                    <MemberScheduleWidget />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    renderAdminPmDashboard() {
        const { t, commonStore, userStore } = this.props;
        const { usersCounters, overallWorkhours, timesheetCounters } = this.state;
        const { total_normal: totalNormal = 0, total_overtime: totalOvertimes = 0,
            total_costs: totalCosts = 0, total_fixed_costs: totalFixedCosts = 0 } = overallWorkhours;

        const { working_now: workingNow = 0, gone_home: goneHome = 0, all_users: allUsers = 0 } = usersCounters;
        const { pending_timesheet: pendingTimesheet = 0, all_timesheets: allTimesheets = 0 } = timesheetCounters;
        const { data: biztypeData } = commonStore.config.biztype;
        const userType = userStore.currentUser.user_type;
        let _totalCosts = parseFloat(totalCosts + totalFixedCosts).toFixed(2);

        const userWorklogStatusData = {
            "title": "Members",
            "metaData": ["Working now", "Gone home", "Not at work", "Pending Timesheet"],
            "data": [Number(workingNow), Number(goneHome), Number((allUsers - goneHome) - workingNow), Number(pendingTimesheet)],
            "dataInPercentatge": [...getPercentageFromNumberValues([Number(workingNow), Number(goneHome), Number((allUsers - goneHome) - workingNow)]), getPercentage(Number(allTimesheets), Number(pendingTimesheet))],
            "dataColors": ["51459E", "7a7b83", "EE2222", "ffa534"],
            "backgroundColor": ["E1E1E1", "ffa534", "F3DDDD", "ffa534"]
        }

        const dateTimeRules = commonStore.getDateTimeRules();
        const greetings = "Time & Control";

        return (
            <>
                <Container className='dashboard-container ps-0' fluid >
                    <Row>
                        <span className='dashboard-title mx-2'>{t('Dashboard')}</span>
                        <p className='dashboard-greeting mx-2'>{t('Hi')} {userStore.currentUser.first_name}! {t('Welcome')} {t('Back')} {t('To')} {greetings}</p>
                    </Row>
                    <Row className='mx-4 mx-md-0'>
                        {userType === 'pm' && <TimelogWidget />}
                        <Col lg={3} sm={{ span: 6, offset: 0 }} xs={{ span: 10, offset: 1 }} className="d-flex justify-content-center d-md-block" >
                            <RectangularCard isFullImage="true" backgoundClass="rectangle-card-bg-white">
                                {
                                    commonStore.config.client.image ? (
                                        <img
                                            src={getUploadedFileURL(commonStore.config.client.image)}
                                            className="img-fluid card-bg-img"
                                            alt='client'
                                        />
                                    ) : (
                                        <div className='d-flex align-items-center fw-bold card-bg-img justify-content-center'>{commonStore.config.client.name}</div>
                                    )
                                }
                            </RectangularCard>
                        </Col>
                        <Col lg={3} sm={{ span: 6, offset: 0 }} xs={{ span: 10, offset: 1 }} className="d-flex justify-content-center d-md-block" >
                            <RectangularCard backgoundClass="rectangle-card-bg-FFF6E6">
                                <Row>
                                    <Col xs={5} sm={5} md={5}>
                                        <RectangularCardIcon backgoundClass="rectangle-card-icon-bg-FFE7BD">
                                            <img src={totalHours} alt="totalHours" />
                                        </RectangularCardIcon>
                                    </Col>
                                    <Col xs={7} sm={7} md={7}>
                                        <span className='d-block text-header'>
                                            {t('Total Hours')}
                                        </span>
                                        <span className='d-block text-body'>
                                            {totalTimeFormat(totalNormal || 0, dateTimeRules)}hrs
                                        </span>
                                        <span className='d-block text-footer color-FEAA15'>
                                            {t('This month')}
                                        </span>
                                    </Col>
                                </Row>
                            </RectangularCard>
                        </Col>
                        <Col lg={3} sm={{ span: 6, offset: 0 }} xs={{ span: 10, offset: 1 }} className="d-flex justify-content-center d-md-block">
                            <RectangularCard backgoundClass="rectangle-card-bg-DFF7E9">
                                <Row>
                                    <Col xs={5} sm={5} md={5}>
                                        <RectangularCardIcon backgoundClass="rectangle-card-icon-bg-C2EDE9">
                                            <img src={overtimeHours} alt="overtimeHours" />
                                        </RectangularCardIcon>
                                    </Col>
                                    <Col xs={7} sm={7} md={7}>
                                        <span className='d-block text-header'>
                                            {t('Overtime')}
                                        </span>
                                        <span className='d-block text-body'>
                                            {totalTimeFormat(totalOvertimes || 0, dateTimeRules)}hrs
                                        </span>
                                        <span className='d-block text-footer color-0A89FF'>
                                            {t('This month')}
                                        </span>
                                    </Col>
                                </Row>
                            </RectangularCard>

                        </Col>
                        {userType !== 'pm' && (
                            <Col lg={3} sm={{ span: 6, offset: 0 }} xs={{ span: 10, offset: 1 }} className="d-flex justify-content-center d-md-block">
                                <RectangularCard backgoundClass="rectangle-card-bg-F6DFDF">
                                    <Row>
                                        <Col xs={5} sm={5} md={5}>
                                            <RectangularCardIcon backgoundClass="rectangle-card-icon-bg-FEB8B8">
                                                <img src={totalCostsSvg} alt="totalCosts" />
                                            </RectangularCardIcon>
                                        </Col>
                                        <Col xs={7} sm={7} md={7}>
                                            <span className='d-block text-header'>
                                                {t('Total Costs')}
                                            </span>
                                            <span className='d-block  text-body'>
                                                {get_currency(dateTimeRules) === '$' ?
                                                    `${get_currency(dateTimeRules)} ${_totalCosts}`
                                                    : `${_totalCosts} ${get_currency(dateTimeRules)}`}
                                            </span>
                                            <span className='d-block text-footer color-FF8383'>
                                                {t('Overtime Excluded')}
                                            </span>
                                        </Col>
                                    </Row>
                                </RectangularCard>
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <Col md={12} >
                            <div className="card-header-with-button">
                                <h4 className="card-title mx-2">
                                    <NavLink to="/admin/members" className="dashboard-nav-link">{t('Members')}</NavLink>
                                </h4>
                            </div>
                            <div className="d-flex mt-3 flex-column align-items-center d-md-block">
                                {this.renderStatusCards(userWorklogStatusData, false, true)}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} sm={12}>
                            <AdminPMScheduleWidget />
                        </Col>
                    </Row>
                    {!!biztypeData.enable_deviations && !biztypeData.union_group && (
                        <Row key="deviationWidget" className="deviation">
                            <DeviationWidget />
                        </Row>
                    )}
                </Container>
            </>
        );
    }

    render() {
        const { userStore } = this.props;
        const { isLoading } = this.state;
        if (isLoading) return <LoadingSpinner />;

        const userType = userStore.currentUser.user_type;

        if (userType === 'super-admin')
            return this.renderSuperAdminDashboard();

        if (userType === 'pm' || userType === 'admin')
            return this.renderAdminPmDashboard();

        return this.renderMemberDashboard();
    }
}
export default inject('dashboardStore', 'userStore', 'commonStore')(withLocalization(applicationRouter(observer(Dashboard))));
